import {
  CustomSearchParams,
  decodeDeepLink,
} from "@/components/common/deep-link/deep-link-encoding";
import type { ValidSavedView } from "@/features/saved-views/apply-saved-view";
import {
  selectResolvedSavedView,
  toSavedViewData,
} from "@/features/saved-views/resolve-saved-view";
import { readSavedViewLocationState } from "@/features/saved-views/resolve-share-link";
import { parseFeaturesAndValuesFromUrl } from "@/store/features/features";
import { changeMode, setSavedView } from "@/store/mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { GUID } from "@faro-lotv/foundation";
import type { SavedViewData } from "@faro-lotv/sphere-viewer-contracts";
import { isEqual } from "es-toolkit";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
  type PropsWithChildren,
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

/**
 * The view the app was opened with, before it is checked against the project.
 *
 * An `Error` means the url carried something that is not a link at all — the one
 * failure the app can decide about without the project. Everything else that
 * makes a view unusable resolves to "no view"; see `selectResolvedSavedView`.
 */
type SavedViewCandidate = SavedViewData | Error | undefined;

type SavedViewContext = {
  /** An element to load the sub-tree for, so the view can be resolved */
  requiredElement: GUID | undefined;

  /** The view to open, resolved against the project */
  savedView: ValidSavedView | Error | undefined;
};

const SavedViewContext = createContext<SavedViewContext | undefined>(undefined);

function useSavedViewContext(): SavedViewContext {
  const context = useContext(SavedViewContext);
  if (!context) {
    throw new Error("SavedViewContext is not initialized.");
  }
  return context;
}

/**
 * @returns a provider for the SavedViewContext
 *
 * The context is needed because a view is consumed the moment it is resolved:
 * the link params are stripped from the url, and the location state a share link
 * redirect passed along goes with them. Resolving into the context, and caching
 * in the store, lets the location change freely afterwards.
 *
 * It is not limited to the view the session started with. A second view can
 * arrive later - another share link, or a pick from a list of saved views - so
 * the candidate is read from the current location rather than frozen, and the
 * store cache only answers once no candidate is left.
 */
export function SavedViewContextProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const [searchParams] = useSearchParams();
  const { state: locationState } = useLocation();

  const candidate = useMemo<SavedViewCandidate>(
    () => readSavedViewCandidate(locationState, searchParams),
    [locationState, searchParams],
  );
  const savedView = useResolveSavedView(candidate, searchParams);
  // Frozen, unlike the candidate: this gates the *initial* project load, and must
  // not fall back to undefined once the link has been stripped from the location.
  const [requiredElement] = useState(() => getRequiredElement(candidate));

  const value = useMemo<SavedViewContext>(
    () => ({ savedView, requiredElement }),
    [savedView, requiredElement],
  );

  return <SavedViewContext value={value}>{children}</SavedViewContext>;
}

/**
 * @returns The view the app should open. Undefined means the app was not opened
 * with a view, while an Error identifies a link the app cannot make sense of.
 */
export function useSavedView(): ValidSavedView | Error | undefined {
  return useSavedViewContext().savedView;
}

/**
 * @returns the element whose sub-tree has to be loaded before the view the app
 * was opened with can be resolved
 */
export function useSavedViewRequiredElement(): GUID | undefined {
  return useSavedViewContext().requiredElement;
}

/**
 * @returns the mode the app was opened in, if it was opened with a view
 *
 * Unlike the other hooks this one tolerates a missing provider, so that tracking
 * a project can report the mode without depending on one.
 */
export function useSavedViewMode(): string | undefined {
  const view = useContext(SavedViewContext)?.savedView;
  if (!view || view instanceof Error) return undefined;
  return view.mode;
}

/**
 * Reads the view the app was opened with, from whichever kind of link was used.
 *
 * A short `/share/<id>` link is resolved by its own route, which hands the
 * payload over through the router location state. A legacy deep link encodes the
 * view in the url, and is normalized into the same shape so only one resolver is
 * needed. The location state wins: it is only ever set by a redirect that has
 * already replaced the url it came from.
 *
 * @param locationState The state of the current router location.
 * @param searchParams The current url search params.
 * @returns the view to resolve, if the app was opened with one.
 */
function readSavedViewCandidate(
  locationState: unknown,
  searchParams: URLSearchParams,
): SavedViewCandidate {
  const shared = readSavedViewLocationState(locationState);
  if (shared) return shared;

  const link = decodeDeepLink(searchParams);
  if (!link || link instanceof Error) return link;
  return toSavedViewData(link);
}

/**
 * Resolves the candidate against the project and cleans the link out of the url.
 *
 * The resolved view is cached in the store before the url is touched, because
 * the cleanup also drops the location state: the cache is what keeps the
 * resolution stable once neither source is left. It is also what stops a reload
 * of the cleaned url from opening the shared view again, which is how a legacy
 * link already behaves once its params are gone.
 *
 * @param candidate The view the app was opened with.
 * @param searchParams The current url search params.
 * @returns the resolved view, or the candidate's error.
 */
function useResolveSavedView(
  candidate: SavedViewCandidate,
  searchParams: URLSearchParams,
): ValidSavedView | Error | undefined {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();

  const resolved = useAppSelector(
    selectResolvedSavedView(candidate instanceof Error ? undefined : candidate),
    isEqual,
  );

  useEffect(() => {
    // Only a view that came from the current location has anything to store or to
    // clean up. One answered from the cache is already in the store and left no
    // link behind, so acting on it again would strip the params of whatever the
    // user navigated to since.
    if (!candidate || candidate instanceof Error || !resolved) return;
    dispatch(setSavedView(resolved));

    // Applying the view is the start mode's job, and nothing sends the app back
    // there on its own: the mode outlives a navigation, because only `clearStore`
    // resets it and nothing dispatches that. A share link followed without a full
    // reload would otherwise cache the view and leave the user in the mode they
    // were already in. On a fresh load the app is in the start mode already, so
    // this changes nothing there.
    dispatch(changeMode("start"));

    // Retain the enabled feature flags, and their optional value, in the url
    const enabledFeatures = parseFeaturesAndValuesFromUrl(searchParams).map(
      (feature) =>
        feature.value !== null && feature.value !== ""
          ? `${feature.urlQuery}=${feature.value}`
          : feature.urlQuery,
    );

    // Keep the query param that indicates that the user came from WebEditor
    if (searchParams.has(CustomSearchParams.hbRedirect)) {
      enabledFeatures.push(`${CustomSearchParams.hbRedirect}=`);
    }

    navigate(
      { pathname, search: `?${enabledFeatures.join("&")}` },
      { replace: true },
    );
  }, [candidate, resolved, dispatch, navigate, pathname, searchParams]);

  return candidate instanceof Error ? candidate : resolved;
}

/**
 * @param candidate The view the app was opened with.
 * @returns the id of the element whose sub-tree the project loader has to fetch
 * for the view to be resolvable
 */
function getRequiredElement(candidate: SavedViewCandidate): GUID | undefined {
  if (!candidate || candidate instanceof Error) return undefined;
  return candidate.id ?? candidate.lookAtId;
}
